import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, Tag, Newspaper } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { Badge } from "@/components/ui/badge";
import { POSTS } from "@/lib/blogPosts";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function BlogIndex() {
  const sorted = [...POSTS].sort((a, b) => b.date.localeCompare(a.date));
  const [featured, ...rest] = sorted;

  return (
    <div data-testid="blog-index-page" className="min-h-screen bg-background text-foreground">
      <Seo
        title="Repair Point Blog — PC & Mac repair guides, security tips"
        description="Practical guides from Repair Point's senior technicians: virus warning signs, slow-PC fixes, macOS troubleshooting, antivirus advice and data recovery best practice."
        path="/blog"
        type="website"
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="border-b border-border bg-secondary">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
            <p className="text-overline text-primary inline-flex items-center gap-2">
              <Newspaper className="h-3.5 w-3.5" /> Repair Point Journal
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] mt-4 max-w-3xl">
              Honest guides from the workshop bench.
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-5 max-w-2xl leading-relaxed">
              Vendor-neutral, no-fluff advice on PC &amp; Mac repair, malware,
              performance and data recovery — written by the same senior
              technicians who fix the machines.
            </p>
          </div>
        </section>

        {/* Featured */}
        <section className="px-6 md:px-12 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-card-${featured.slug}`}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center border border-border rounded-md overflow-hidden bg-card hover:shadow-lg transition-shadow"
            >
              <div className="lg:col-span-7 overflow-hidden">
                <img
                  src={featured.cover}
                  alt={featured.coverAlt}
                  className="w-full h-[280px] md:h-[420px] object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="lg:col-span-5 p-7 md:p-10">
                <p className="text-overline text-primary">Featured</p>
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mt-3 leading-tight">
                  {featured.title}
                </h2>
                <p className="text-sm md:text-base text-muted-foreground mt-4 leading-relaxed">
                  {featured.description}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" /> {formatDate(featured.date)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" /> {featured.readTime} min read
                  </span>
                </div>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-7 group-hover:gap-2.5 transition-all">
                  Read article <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          </div>
        </section>

        {/* Grid */}
        <section className="px-6 md:px-12 pb-20 md:pb-28">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">More articles</h2>
              <span className="text-sm text-muted-foreground">{rest.length} posts</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-px bg-border border border-border rounded-md overflow-hidden">
              {rest.map((p, i) => (
                <motion.article
                  key={p.slug}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-60px" }}
                  custom={i}
                  variants={fadeUp}
                  className="bg-card"
                >
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group block p-7 md:p-8 h-full hover:bg-secondary/40 transition-colors"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {p.tags.slice(0, 2).map((t) => (
                        <Badge key={t} variant="outline" className="rounded-full text-[10px] font-medium">
                          <Tag className="h-2.5 w-2.5 mr-1" /> {t}
                        </Badge>
                      ))}
                    </div>
                    <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-tight mt-4 leading-tight group-hover:text-primary transition-colors">
                      {p.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{p.description}</p>
                    <div className="mt-6 pt-5 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" /> {formatDate(p.date)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" /> {p.readTime} min
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
